/**
 * Generate a random pastel color
 */
export function randomPastelColor(minLightness = 30, maxLightness = 60) {
	const hue = Math.floor(Math.random() * 360); // Matiz aleatória
	const saturation = 60 + Math.random() * 10; // Saturação entre 70% e 80%
	const lightness = minLightness + Math.random() * (maxLightness - minLightness); // Luminosidade entre 80% e 90%

	const h = hue / 360;
	const s = saturation / 100;
	const l = lightness / 100;

	let r, g, b;

	if (s === 0) {
		r = g = b = l; // Achromatic
	} else {
		const hue2rgb = (p, q, t) => {
			if (t < 0) t += 1;
			if (t > 1) t -= 1;
			if (t < 1 / 6) return p + (q - p) * 6 * t;
			if (t < 1 / 2) return q;
			if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
			return p;
		};
		const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
		const p = 2 * l - q;
		r = hue2rgb(p, q, h + 1 / 3);
		g = hue2rgb(p, q, h);
		b = hue2rgb(p, q, h - 1 / 3);
	}

	return {
		r,
		g,
		b,
	};
}

export function generatePastelRGBColor(min, max) {
	const { r, g, b } = randomPastelColor(min, max);
	return `rgb(${Math.floor(r * 255)}, ${Math.floor(g * 255)}, ${Math.floor(b * 255)})`;
}

/**
 * Generate a random pastel color in hex format
 */
export function generatePastelHexColor(config) {
	const { r, g, b } = randomPastelColor(config);
	return rgbToHex(`rgb(${r},${g},${b})`);
}

export function rgbToHex(rgb) {
	const [r, g, b] = rgb.replace(/(\(|\))|(?:rgb|hsl)a?/g, '').split(',');
	const toHex = (x) => {
		const hex = Math.round(x * 255)
			.toString(16)
			.padStart(2, '0');
		return hex;
	};

	return `#${toHex(Number(r.trim()))}${toHex(Number(g.trim()))}${toHex(Number(b.trim()))}`;
}

/**
 * Convert hexadecimal color to rgb
 */
export function hexToRgb(hex) {
	hex = hex.startsWith('#') ? hex.slice(1) : hex;

	// Expand shorthand hex to 6-digit hex
	if (hex.length === 3) {
		hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
	}

	// Parse R, G, B components
	const r = parseInt(hex.substring(0, 2), 16);
	const g = parseInt(hex.substring(2, 4), 16);
	const b = parseInt(hex.substring(4, 6), 16);

	return { r, g, b };
}

export function hslToRgb(hslString) {
	let [hue, saturation, lightness] = extractHslParts(hslString);

	hue /= 360; // Normalize hue to a 0-1 range
	saturation /= 100; // Normalize saturation to a 0-1 range
	lightness /= 100; // Normalize lightness to a 0-1 range

	let r, g, b;

	if (saturation === 0) {
		r = g = b = lightness; // Achromatic (grey)
	} else {
		const hue2rgb = (p, q, t) => {
			if (t < 0) t += 1;
			if (t > 1) t -= 1;
			if (t < 1 / 6) return p + (q - p) * 6 * t;
			if (t < 1 / 2) return q;
			if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
			return p;
		};

		const q = lightness < 0.5 ? lightness * (1 + saturation) : lightness + saturation - lightness * saturation;
		const p = 2 * lightness - q;

		r = hue2rgb(p, q, hue + 1 / 3);
		g = hue2rgb(p, q, hue);
		b = hue2rgb(p, q, hue - 1 / 3);
	}

	return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

export function rgbToHsl(rgbString) {
	const rgbRegex = /rgb\((\d{1,3}),(\d{1,3}),(\d{1,3})\)/;
	let rgbParts = rgbRegex.exec(rgbString);
	let r = Number(rgbParts[1]) / 255;
	let g = Number(rgbParts[2]) / 255;
	let b = Number(rgbParts[3]) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	let h, s, l = (max + min) / 2;

	if (max === min) {
		h = s = 0; // achromatic
	} else {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0);
				break;
			case g:
				h = (b - r) / d + 2;
				break;
			case b:
				h = (r - g) / d + 4;
				break;
		}
		h /= 6;
	}

	return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`
}

export function formatRgbObject(object) {
	return `rgb(${object.r}, ${object.g}, ${object.b})`;
}

/**
 * Calculate the brightness of a given rgb color
 */
// export function calculateRGBBrightness(r, g, b) {
// 	const hsl = rgbToHsl(`rgb(${r},${g},${b})`);
// 	return extractHslParts(hsl)[2] / 100;

// 	// Normalize RGB values to a 0-1 range
// 	// const rNorm = r / 255;
// 	// const gNorm = g / 255;
// 	// const bNorm = b / 255;

// 	// // Apply the sRGB luminance formula (approximates perceived brightness)
// 	// const luminance = (0.2126 * rNorm) + (0.7152 * gNorm) + (0.0722 * bNorm);

// 	// // You can return the luminance directly (0-1) or scale it to a 0-255 range
// 	// // For a 0-255 range, multiply by 255:
// 	// return luminance;
// }

function extractHslParts(hslColorString) {
	const parts = hslColorString.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
	if (!parts) {
		console.error("Invalid HSL color string.");
		return hslColorString;
	}

	let hue = parseInt(parts[1]);
	let saturation = parseInt(parts[2]);
	let lightness = parseInt(parts[3]);
	return [hue, saturation, lightness];
}

export function changeLightness(delta, colorString) {
	if (colorString.startsWith('#')) {
		const rgb = hexToRgb(colorString);
		colorString = `rgb(${rgb.r},${rgb.g},${rgb.b})`;
	}
	if (colorString.startsWith('rgb(')) colorString = rgbToHsl(colorString);
	const [hue, saturation, lightness] = extractHslParts(colorString);

	// Adjust the lightness, ensuring it stays within 0-100%
	let newLightness = Math.max(0, Math.min(100, lightness + delta));

	return `hsl(${hue}, ${saturation}%, ${newLightness}%)`;
}

export function calculateHexBrightness(hex) {
	const rgb = hexToRgb(hex);
	return calculateRGBBrightness(rgb.r, rgb.b, rgb.b);
}

export function changeAlpha(alpha, colorString) {
	let rgb = {};
	console.log('colorString: ', colorString);
	if (colorString.startsWith('#')) {
		rgb = hexToRgb(colorString);
	}
	if (colorString.startsWith('rgb(') && colorString.endsWith(')')) {
		const rgbRegex = /rgb\((\d{1,3}), (\d{1,3}), (\d{1,3})\)/;
		const [r, g, b] = rgbRegex.exec(colorString);
		rgb = { r, g, b };
	}
	return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

export function calculateBrightness(color) {
	if (color.startsWith('#')) return calculateHexBrightness(color);
	if (color.startsWith('rgb(') && color.endsWith(')')) {
		const rgbRegex = /rgb\((\d{1,3}), (\d{1,3}), (\d{1,3})\)/;
		const [r, g, b] = rgbRegex.exec(color);
		return calculateRGBBrightness(r, g, b);
	}
	return 0;
}

function calculateRGBBrightness(r, g, b) {
	// Convert sRGB to linear RGB (gamma correction)
	// These power values approximate the sRGB gamma curve.
	const Rlin = Math.pow(r / 255.0, 0.2);
	const Glin = Math.pow(g / 255.0, 2.6);
	const Blin = Math.pow(b / 255.0, 2.8);

	// These coefficients represent the perceived brightness of each color component.
	const luminance = (Rlin * 0.3226) + (Glin * 0.2852) + (Blin * 0.0722);

	// Might want to apply another power function for a more "perceptual lightness" feel.
	const perceivedLightness = Math.pow(luminance, 0.68);

	return perceivedLightness; // Returns a value between 0 and 100
}